/**
 * O recap da semana de liga — o que a virada fez com você.
 *
 * ## Por que este módulo existe
 *
 * O único aviso de fim de semana que o produto tinha era o
 * `LeaguePromotionModal`, que abria **direto o Share Studio** e só disparava em
 * PROMOÇÃO (`isTierUp`). Ou seja: quem ficou no meio ou caiu de liga não recebia
 * sinal nenhum de que a semana tinha fechado, e quem subiu recebia um editor de
 * card sem antes ler o que aconteceu. Aqui mora a leitura do fato; a casca do
 * Studio passa a ser a SEGUNDA ação, não a primeira.
 *
 * ## As três guardas, e por que cada uma é necessária
 *
 * 1. **Frescor.** `lastClosedWeek` é histórico: para quem não abre o app há um
 *    mês, ele aponta para um fechamento de semanas atrás. Anunciar "sua semana
 *    fechou" sobre aquilo é falso. A régua é a mesma do
 *    `isGlobalRankingFresh` — a última semana ISO fechada, e nenhuma outra.
 * 2. **Completude.** Doc gravado ANTES desta leva tem `lastClosedWeek` e não tem
 *    `lastClosedOutcome`. Ler os campos ausentes como zero renderizaria "0 XP ·
 *    0 moedas" sobre uma semana real. `outcome` é o marcador de versão do
 *    recap: sem ele, não há recap.
 * 3. **Elite carimbada por semana.** `users.eliteRank` NÃO serve aqui — ele só é
 *    reescrito para quem está no top e o selo vale 8 dias, então quem foi Elite
 *    na semana passada e não nesta continua com o campo preenchido e ainda
 *    fresco. O posto só conta quando `lastClosedEliteWeek` é a semana do
 *    fechamento.
 */

import { usesScoreRegime } from "./leagueRules";
import {
  LEAGUE_LABELS,
  type LeagueSummary,
  type LeagueTier,
  type RankingMetric,
} from "../../../services/firebase/leagueService";
import { previousISOWeekKey } from "../../../utils/isoWeek";

export type LeagueOutcome = "promoted" | "relegated" | "stayed";

export interface LeagueWeekRecap {
  /** Semana ISO fechada (ex.: "2026_W34"). */
  week: string;
  /** Liga em que a semana foi disputada. */
  tierClosed: LeagueTier;
  /** Liga em que a pessoa está a partir de agora. */
  newTier: LeagueTier;
  outcome: LeagueOutcome;
  position: number;
  totalMembers: number;
  score: number;
  metric: RankingMetric;
  /** Moedas creditadas pelo prêmio — já com o bônus PRO, zero se não houve. */
  coins: number;
  /** Posto no top global daquela semana, ou `null`. */
  eliteRank: number | null;
  /**
   * Conquistas que ESTE fechamento deu de novo — e, por trás delas, o
   * cosmético.
   *
   * ⚠️ Vem do servidor já filtrado por "é nova": `leagueBadgesFromSummary`
   * devolve as badges cujo limiar está batido, então uma lista crua faria o
   * cartão anunciar "Veterano da Liga" todo domingo depois da quarta semana. O
   * diff é feito na transação do fechamento, onde o estado anterior existe.
   *
   * Vazio é o caso comum — só quem chega ao pódio ou cruza um limiar de
   * consistência ganha algo além de moeda.
   */
  badges: string[];
  /**
   * A sala decidiu por PISO de pontuação, não por posição.
   *
   * Quem responde é `usesScoreRegime`, a MESMA função que a virada consulta —
   * repetir a condição aqui abriria a porta de a tela pintar uma posição que
   * não decidiu nada. Em regime de piso o número a mostrar é o placar: das 23
   * salas públicas já fechadas em produção, 14 tinham um único membro, e "1º de
   * 1" é placar verdadeiro e informação nenhuma.
   */
  scoreRegime: boolean;
}

export function resolveLeagueWeekRecap(
  summary: LeagueSummary | null,
  now: Date = new Date(),
): LeagueWeekRecap | null {
  if (!summary) return null;

  const week = summary.lastClosedWeek;
  if (!week || week !== previousISOWeekKey(now)) return null;

  const outcome = summary.lastClosedOutcome;
  const tierClosed = summary.lastClosedTier;
  if (!outcome || !tierClosed) return null;

  const totalMembers = summary.lastClosedTotalMembers ?? 0;
  const metric: RankingMetric = summary.lastClosedMetric ?? "xp";

  return {
    week,
    tierClosed,
    newTier: summary.lastClosedNewTier ?? tierClosed,
    outcome,
    position: summary.lastClosedPosition ?? 0,
    totalMembers,
    score: summary.lastClosedScore ?? 0,
    metric,
    coins: summary.lastClosedCoins ?? 0,
    eliteRank:
      summary.lastClosedEliteWeek === week ? summary.lastClosedEliteRank : null,
    badges: summary.lastClosedBadges,
    scoreRegime: usesScoreRegime(totalMembers, metric),
  };
}

// ── Copy do cartão ───────────────────────────────────────────────────────────

export interface LeagueRecapHeadline {
  eyebrow: string;
  title: string;
  subtitle: string;
}

/**
 * As três linhas do topo do cartão.
 *
 * Cada uma responde uma pergunta diferente, e nenhuma repete a outra:
 * **sobrancelha** = o que aconteceu · **título** = onde você está a partir de
 * agora · **frase** = como foi a semana, e em qual liga ela foi disputada.
 *
 * ⚠️ A primeira versão fechava a frase com a liga NOVA — e o render mostrou o
 * nome dela duas vezes na mesma tela, a 30px de distância do título. Nenhuma
 * medida de contraste ou de alvo pega isso; só olhar.
 *
 * O TOM é decisão, e é o pedido: subir é comemoração, cair não é. O cartão de
 * rebaixamento usa a mesma forma do de promoção — sem confete, sem "parabéns" —
 * porque o produto precisa contar a semana inteira, não só a metade boa.
 *
 * `scoreRegime` decide qual número vira frase. Em sala de menos de 5 pessoas a
 * posição não mede a semana (mede quantas apareceram), e o caso é o dominante
 * hoje: 14 das 23 salas públicas já fechadas tinham UM membro. "Você ficou em 1º
 * de 1" é verdade e não diz nada.
 */
export function buildRecapHeadline(recap: LeagueWeekRecap): LeagueRecapHeadline {
  const closed = LEAGUE_LABELS[recap.tierClosed];
  const next = LEAGUE_LABELS[recap.newTier];

  const placement = recap.scoreRegime
    ? `Você fechou com ${recap.score.toLocaleString("pt-BR")} XP`
    : `Você ficou em ${recap.position}º de ${recap.totalMembers}`;

  if (recap.outcome === "promoted") {
    return {
      eyebrow: "Você subiu de liga",
      title: next,
      subtitle: `${placement} na ${closed}.`,
    };
  }

  if (recap.outcome === "relegated") {
    return {
      eyebrow: "Você caiu de liga",
      title: next,
      subtitle: `${placement} na ${closed}. Dá pra voltar na próxima.`,
    };
  }

  return {
    eyebrow: "Semana fechada",
    title: closed,
    subtitle: `${placement}.`,
  };
}
